import { useBaxterChanged } from '@/lib/baxterEffects';
import { useTurnHighlight } from '@/lib/useTurnHighlight';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useContentTypes, useDeleteContentType } from '@wordpuppi/shared';
import type { ContentType } from '@wordpuppi/shared';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/LoadingSkeleton';
import { confirmDialog } from '@/lib/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TypeIcon } from '@/components/TypeIcon';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { UnderlineTabs } from '@/components/sub-components/UnderlineTabs';
import { Plus, Pencil, Trash2, Boxes } from 'lucide-react';

export const Route = createFileRoute('/sites/$siteSlug/content-types/')({
  component: ContentTypesPage,
});

type TabType = 'custom' | 'system';

function ContentTypesPage() {
  const { siteSlug } = Route.useParams();
  const { data, isLoading, error } = useContentTypes(siteSlug);
  const flash = useTurnHighlight(data?.data?.map((row) => ({ id: String(row.id), name: row.name, slug: row.slug, icon: row.icon, singleton: row.singleton, count: row.item_count })), useBaxterChanged('content-types'));
  const deleteContentType = useDeleteContentType(siteSlug);
  const [activeTab, setActiveTab] = useState<TabType>('custom');

  const handleDelete = async (slug: string, name: string, isSystem: boolean, itemCount: number) => {
    if (isSystem) {
      toast.error('Cannot delete system content types');
      return;
    }

    const message = itemCount > 0
      ? `Delete "${name}"? This will permanently delete ${itemCount} items. This cannot be undone.`
      : `Delete "${name}"?`;

    if (await confirmDialog(message, { destructive: true, confirmLabel: 'Delete type' })) {
      try {
        await deleteContentType.mutateAsync({ typeSlug: slug, force: itemCount > 0 });
        toast.success('Content type deleted');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to delete content type');
      }
    }
  };

  // Split types into custom and system
  const allTypes = data?.data || [];
  const customTypes = allTypes.filter((t) => !t.is_system);
  const systemTypes = allTypes.filter((t) => t.is_system);
  const displayedTypes = activeTab === 'custom' ? customTypes : systemTypes;

  const tabs = [
    { value: 'custom' as const, label: 'Custom Types', count: customTypes.length },
    { value: 'system' as const, label: 'System Types', count: systemTypes.length },
  ];

  const renderTable = (types: ContentType[], showDelete: boolean) => {
    if (types.length === 0) {
      return (
        <div className="text-center py-16 px-6 rounded-xl border-2 border-dashed border-border">
          <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
            <Boxes className="h-6 w-6 text-muted-foreground" />
          </div>
          {activeTab === 'custom' ? (
            <>
              <p className="text-lg font-medium">No custom types yet</p>
              <p className="text-muted-foreground mt-1 mb-6">
                Custom types let you model anything your site needs — projects, recipes, testimonials.
              </p>
              <Button asChild>
                <Link to="/sites/$siteSlug/content-types/new" params={{ siteSlug }}>
                  Create your first type
                </Link>
              </Button>
            </>
          ) : (
            <>
              <p className="text-lg font-medium">No system types found</p>
              <p className="text-muted-foreground mt-1">
                Built-in types appear here once your site is set up.
              </p>
            </>
          )}
        </div>
      );
    }

    return (
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Items</TableHead>
              {activeTab === 'custom' && <TableHead className="w-24 text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {types.map((type) => (
              <TableRow key={type.id} className={flash.has(String(type.id)) ? 'baxter-changed' : undefined}>
                <TableCell>
                  <Link
                    to="/sites/$siteSlug/content-types/$typeSlug"
                    params={{ siteSlug, typeSlug: type.slug }}
                    className="inline-flex items-center gap-2 text-foreground font-medium hover:underline"
                  >
                    <TypeIcon icon={type.icon} className="size-4 text-muted-foreground" />
                    {type.name}
                  </Link>
                  {type.singleton && (
                    <Badge variant="secondary" className="ml-2">
                      Singleton
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground font-mono">
                  {type.slug}
                </TableCell>
                <TableCell>{type.item_count}</TableCell>
                {activeTab === 'custom' && (
                  <TableCell>
                    <div className="flex gap-1 justify-end">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-muted-foreground hover:text-foreground"
                        title="Edit type"
                        asChild
                      >
                        <Link
                          to="/sites/$siteSlug/content-types/$typeSlug"
                          params={{ siteSlug, typeSlug: type.slug }}
                        >
                          <Pencil />
                          <span className="sr-only">Edit</span>
                        </Link>
                      </Button>
                      {showDelete && (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-muted-foreground hover:text-destructive"
                          title="Delete type"
                          onClick={() => handleDelete(type.slug, type.name, type.is_system, type.item_count)}
                          disabled={deleteContentType.isPending}
                        >
                          <Trash2 />
                          <span className="sr-only">Delete</span>
                        </Button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="max-w-[1500px]">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Custom Types</h1>
        <Button asChild>
          <Link to="/sites/$siteSlug/content-types/new" params={{ siteSlug }}>
            <Plus />
            New Type
          </Link>
        </Button>
      </div>

      <UnderlineTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Tab description */}
      <p className="text-muted-foreground text-sm mb-4">
        {activeTab === 'custom'
          ? 'Custom content types you create for your site.'
          : 'Built-in system types. You can extend these with custom fields but cannot delete them.'}
      </p>

      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <div className="text-center py-12 text-muted-foreground">Error loading content types</div>
      ) : !data?.data ? (
        <div className="text-center py-12 text-muted-foreground">Error: Failed to load content types</div>
      ) : (
        renderTable(displayedTypes, activeTab === 'custom')
      )}
    </div>
  );
}
